import { useAbility } from "@casl/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";

import { type AppAbility, documentEventChannel } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import type { DocumentRecord, ListDocumentsResponse } from "@recobra/schemas/document";

import { isClassificationEvent } from "../lib";
import { useAuth } from "../lib";
import { getDocumentFailureDescription } from "./DocumentFailureDescription";
import { useDocumentEvents } from "./useDocumentEvents";

/**
 * Subscribes to AppSync Events for the current user's notification channel
 * on a case. Updates the documents query cache in real-time as classification
 * completes. Shares the underlying connection with any other useDocumentEvents
 * subscribers on the same channel (e.g. UploadArea).
 */
export const useRealtimeClassification = (caseId: string | undefined) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const ability = useAbility<AppAbility>();
  const hasPermission = ability.can("read", "Document");

  const channel = caseId && user?.userId ? documentEventChannel(caseId, user.userId) : undefined;

  const onEvent = useCallback(
    (payload: unknown) => {
      if (!isClassificationEvent(payload)) return;
      const data = payload;

      queryClient.setQueryData<ListDocumentsResponse>(
        ["cases", caseId, "documents"],
        (old: ListDocumentsResponse | undefined) => {
          if (!old) return old;

          return {
            items: old.items.map((doc: DocumentRecord) => {
              if (doc.id !== data.documentId) return doc;
              if (data.status === "uploaded") {
                return { ...doc, status: "uploaded" as const };
              }
              if (data.status === "classifying") {
                return { ...doc, status: "classifying" as const };
              }
              if (data.status === "classified") {
                return {
                  ...doc,
                  status: "classified" as const,
                  ...(data.classification ? { classification: data.classification } : {}),
                  ...(data.videoAnalysis ? { videoAnalysis: data.videoAnalysis } : {}),
                  ...(data.thumbnailKey ? { thumbnailKey: data.thumbnailKey } : {}),
                };
              }
              if (data.status === "failed") {
                return {
                  ...doc,
                  status: "failed" as const,
                  failureReason: data.failureReason,
                };
              }

              return doc;
            }),
          };
        },
      );

      if (data.status === "classified" || data.status === "uploaded") {
        void queryClient.invalidateQueries({
          queryKey: ["cases", caseId, "documents"],
        });
      }

      if (data.status === "classified" && hasPermission && data.classification) {
        toast.success(m.doc_classification_success(), {
          description: `${m.document_type_label({
            documentType: data.classification.documentType,
          })} — ${m.upload_confidence({ level: data.classification.confidence })}`,
        });
      }

      if (data.status === "failed") {
        if (hasPermission) {
          toast.error(m.doc_classification_failed(), {
            description: getDocumentFailureDescription(data.failureReason),
          });
        }
      }
    },
    [queryClient, caseId, hasPermission],
  );

  useDocumentEvents(channel, onEvent);
};
